// src/components/DietAssessment/index.js

import React, { useContext, useState, Suspense, lazy, useEffect } from 'react';
import { Box, Typography, Fab, Menu, MenuItem } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { ProgressDisplay } from './components/ProgressDisplay';
import { CenteredLoader } from '../../App';
import { Build } from '@mui/icons-material';
import { toCapitalizeFromUnderScore, isLocalhost } from '../../lib/helpers/helpers';
import { AssessmentNextStepButton } from './components/AssessmentNextStepButton';
import AssessmentStepErrorBoundary from './components/AssessmentStepErrorBoundary';
import { AssessmentContext, AssessmentContextProvider } from '../../lib/contexts';

const stepComponentMapping = {
  custom_user_consent: lazy(() => import('./components/CustomConsent')),
  dob_check: lazy(() => import('./components/DOBCheck')),
  diet_screener: lazy(() => import('./components/Screener')),
  photo_selection: lazy(() => import('./components/PhotoSelection')),
  diet_results: lazy(() => import('./components/DietResults')),
  id_to_ideal_results: lazy(() => import('./components/DietResults')), // now points to DietResults
  ideal_goals: lazy(() => import('./components/Ideal')),
  multi_radio_select: lazy(() => import('./components/MultiRadioSelect')),
  question_select: lazy(() => import('./components/QuestionSelect')),
  question_select_dropdown: lazy(() => import('./components/QuestionSelectDropdown')),
  question_multiple_select: lazy(() => import('./components/QuestionMultipleSelect')),
  question_usps_address: lazy(() => import('./components/QuestionUSPSAddress')),
  question_input: lazy(() => import('./components/QuestionInput')),
  user_info: lazy(() => import('./components/UserInfo')),
};

const stepPropMapping = {
  diet_results: { variant: 'diet' },
  id_to_ideal_results: { variant: 'transition' },
  // add more overrides here as needed
};

const DefaultStep = lazy(() => import('./components/DefaultStep'));

const DietAssessment = () => {
  const { step: routeStep, workflowId: routeWorkflowId } = useParams();
  const navigate = useNavigate();

  const { state, actions } = useContext(AssessmentContext);
  const {
    currentStepInfo,
    stepOverride,
    responses,
    progress,
    loadingNext,
    stepError,
    nextVisible,
    nextAllowed,
    isLoading,
    error,
  } = state;
  const { setCurrentStepChoices, setStepOverride, handleNextStep } = actions;

  const [anchorEl, setAnchorEl] = useState(null);

  const effectiveStep = stepOverride || routeStep || (currentStepInfo && currentStepInfo.step);
  const effectiveWorkflowId = routeWorkflowId;

  const StepComponent = stepComponentMapping[currentStepInfo?.type] || DefaultStep;
  const extraProps = stepPropMapping[currentStepInfo?.type] || {};

  if (isLoading) return <CenteredLoader />;
  if (error) return <Typography color="error">{error.message}</Typography>;

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', mt: 4 }}>
      {progress.length > 0 && <ProgressDisplay progress={progress} />}
      {loadingNext && <CenteredLoader />}
      {stepError && <Typography color="error">{stepError.message}</Typography>}

      <AssessmentStepErrorBoundary onGoBack={() => navigate(-1)}>
        <Suspense fallback={<CenteredLoader />}>
          {currentStepInfo && (
            <>
              {currentStepInfo?.title && <Typography variant={'subtitle1'}>{currentStepInfo?.title}</Typography>}
              {currentStepInfo?.heading && <Typography variant={'subtitle2'}>{currentStepInfo?.heading}</Typography>}
              {currentStepInfo?.sub_heading && (
                <Typography variant={'body1'}>{currentStepInfo?.sub_heading}</Typography>
              )}
              <StepComponent
                stepInfo={{ ...currentStepInfo, step: effectiveStep }}
                onChange={setCurrentStepChoices}
                responses={responses}
                {...currentStepInfo}
                {...extraProps}
              />
            </>
          )}
        </Suspense>
      </AssessmentStepErrorBoundary>

      <AssessmentNextStepButton
        nextVisible={nextVisible}
        nextAllowed={nextAllowed}
        stepInfo={currentStepInfo}
        onSubmit={handleNextStep}
      />

      {isLocalhost() && (
        <>
          <Fab
            sx={{ position: 'fixed', bottom: 80, right: 28, width: 32, height: 32 }}
            onClick={(event) => setAnchorEl(event.currentTarget)}
          >
            <Build />
          </Fab>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
          >
            {Object.keys(stepComponentMapping).map((stepKey) => (
              <MenuItem
                key={stepKey}
                onClick={() => {
                  setAnchorEl(null);
                  setStepOverride(stepKey);
                  navigate(`/assessment/${effectiveWorkflowId}/${stepKey}`, { replace: true });
                }}
              >
                <Typography variant="body2">{toCapitalizeFromUnderScore(stepKey)}</Typography>
              </MenuItem>
            ))}
          </Menu>
        </>
      )}
    </Box>
  );
};

const WrappedDietAssessment = () => (
  <AssessmentContextProvider>
    <DietAssessment />
  </AssessmentContextProvider>
);

export default WrappedDietAssessment;
